import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Check, 
  X, 
  Clock,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Activity,
  AlertCircle,
  RefreshCw,
  User,
  TrendingUp
} from 'lucide-react';
import { timesheetAPI } from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import toast from 'react-hot-toast';

const TimesheetReview = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fetchNotifications } = useNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL'); // Add role filter
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);

  const { data: timesheetsData, isLoading, refetch } = useQuery(
    ['admin-timesheets', currentPage, pageSize],
    () => timesheetAPI.getAllTimesheets({ page: currentPage, size: pageSize }),
    {
      select: (response) => response.data,
      staleTime: 1 * 60 * 1000, // Cache for 1 minute
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchInterval: 60000, // Refetch every 60 seconds (reduced from 30)
      refetchIntervalInBackground: false, // Disable background refetch for better performance
      onSuccess: () => {
        setLastUpdateTime(new Date());
      }
    }
  );

  const timesheets = timesheetsData?.timesheets || [];
  const totalPages = timesheetsData?.totalPages || 0;
  const total = timesheetsData?.total || 0;

  const approveMutation = useMutation(
    (timesheetId) => timesheetAPI.approveTimesheet(timesheetId),
    {
      onSuccess: () => {
        toast.success('Timesheet approved successfully');
        queryClient.invalidateQueries(['admin-timesheets', currentPage, pageSize]);
        // Refresh notifications after approval (creates notification for employee)
        fetchNotifications();
        setShowDetails(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    ({ timesheetId, reason }) => timesheetAPI.rejectTimesheet(timesheetId, reason),
    {
      onSuccess: () => {
        toast.success('Timesheet rejected successfully');
        queryClient.invalidateQueries(['admin-timesheets', currentPage, pageSize]);
        // Refresh notifications after rejection (creates notification for employee)
        fetchNotifications();
        setShowDetails(false);
        setShowRejectionModal(false);
        setRejectionReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );

  const filteredTimesheets = timesheets?.filter(timesheet => {
    // Role filter
    if (roleFilter !== 'ALL') {
      const employeeRole = timesheet.employee?.role;
      if (roleFilter === 'EMPLOYEE' && employeeRole !== 'EMPLOYEE') {
        return false;
      }
      if (roleFilter === 'MANAGER' && employeeRole !== 'MANAGER') {
        return false;
      }
    }
    
    // Status filter
    const matchesStatus = statusFilter === 'ALL' || timesheet.status === statusFilter;
    if (!matchesStatus) {
      return false;
    }
    
    // Check if searching by timesheet ID
    const hasTimesheetIdSearch = timesheetIdSearch && timesheetIdSearch.trim() !== '';
    
    // If searching by ID, check ID match first
    if (hasTimesheetIdSearch) {
      const searchId = String(timesheetIdSearch).trim();
      const timesheetId = timesheet.id ? String(timesheet.id) : '';
      if (!timesheetId.includes(searchId)) {
        return false; // ID doesn't match, exclude this timesheet
      }
      // ID matches, continue with other filters (date)
    } else {
      // Not searching by ID, check employee and manager search
      const hasSearch = searchTerm && searchTerm.trim() !== '';
      if (hasSearch) {
        const searchLower = searchTerm.toLowerCase();
        const matchesEmployee = 
          timesheet.employee?.firstName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.lastName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.email?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.username?.toLowerCase().includes(searchLower);
        
        // Also search in manager names (assignedByManagerNames)
        const matchesManager = timesheet.assignedByManagerNames?.toLowerCase().includes(searchLower) || false;
        
        if (!matchesEmployee && !matchesManager) {
          return false; // Neither employee nor manager search matches
        }
      }
    }
    
    // Date range filtering
    let matchesDateRange = true;
    if (startDate || endDate) {
      // Handle both possible field names for timesheet dates
      const timesheetStartDate = new Date(timesheet.startDate || timesheet.weekStartDate);
      const timesheetEndDate = new Date(timesheet.endDate || timesheet.weekEndDate);
      const filterStartDate = startDate ? new Date(startDate) : null;
      const filterEndDate = endDate ? new Date(endDate) : null;
      
      // Check if dates are valid
      if (isNaN(timesheetStartDate.getTime()) || isNaN(timesheetEndDate.getTime())) {
        console.log('Invalid timesheet dates:', timesheet.startDate, timesheet.endDate);
        matchesDateRange = false;
      } else if (filterStartDate && filterEndDate) {
        // Both dates provided - timesheet should overlap with the range
        // Reset time to start of day for accurate comparison
        const tsStart = new Date(timesheetStartDate.getFullYear(), timesheetStartDate.getMonth(), timesheetStartDate.getDate());
        const tsEnd = new Date(timesheetEndDate.getFullYear(), timesheetEndDate.getMonth(), timesheetEndDate.getDate());
        const fsStart = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        const fsEnd = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        
        matchesDateRange = tsStart <= fsEnd && tsEnd >= fsStart;
      } else if (filterStartDate) {
        // Only start date - timesheet should end on or after this date
        const tsEnd = new Date(timesheetEndDate.getFullYear(), timesheetEndDate.getMonth(), timesheetEndDate.getDate());
        const fsStart = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        matchesDateRange = tsEnd >= fsStart;
      } else if (filterEndDate) {
        // Only end date - timesheet should start on or before this date
        const tsStart = new Date(timesheetStartDate.getFullYear(), timesheetStartDate.getMonth(), timesheetStartDate.getDate());
        const fsEnd = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        matchesDateRange = tsStart <= fsEnd;
      }
    }
    
    return matchesDateRange;
  }) || [];

  // Sort timesheets by last updated
  const sortedTimesheets = [...filteredTimesheets].sort((a, b) => {
    const aValue = new Date(a.updatedAt || a.createdAt);
    const bValue = new Date(b.updatedAt || b.createdAt);
    return bValue - aValue; // Most recent first
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Clock className="w-4 h-4 text-gray-500" />;
      case 'SUBMITTED':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'PENDING':
        return <RefreshCw className="w-4 h-4 text-orange-500" />;
      case 'REUPDATED':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'REUPDATED':
        return 'bg-blue-100 text-blue-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const handleApprove = () => {
    if (selectedTimesheet) {
      approveMutation.mutate(selectedTimesheet.id);
    }
  };

  const handleReject = () => {
    if (selectedTimesheet) {
      setShowRejectionModal(true);
    }
  };

  const handleConfirmReject = () => {
    if (selectedTimesheet && rejectionReason.trim()) {
      rejectMutation.mutate({ 
        timesheetId: selectedTimesheet.id, 
        reason: rejectionReason.trim() 
      });
    } else {
      toast.error('Please provide a reason for rejection');
    }
  };


  const calculateTotalHours = (entries) => {
    return entries?.reduce((total, entry) => total + (entry.hours || entry.hoursWorked || 0), 0) || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-primary-600" />
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-gray-900">Timesheet Review</h1>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 text-green-600">
                    <Activity className="w-4 h-4 animate-pulse" />
                    <span className="text-sm font-medium">Live</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Last updated: {lastUpdateTime.toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 mt-1">
                Real-time review and management of employee timesheets
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
         <div className="flex items-center justify-between mb-4">
           <h3 className="text-lg font-medium text-gray-900">Filters & Search</h3>
         </div>
        
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
           <div className="relative">
             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
             <input
               type="text"
               placeholder="Search by employee/manager name or email..."
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
             />
           </div>
           <div className="relative">
             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
             <input
               type="text"
               placeholder="Search by timesheet ID..."
               value={timesheetIdSearch}
               onChange={(e) => setTimesheetIdSearch(e.target.value)}
               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
             />
           </div>
           
           <select
             value={statusFilter}
             onChange={(e) => setStatusFilter(e.target.value)}
             className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white"
           >
             <option value="ALL">All Status</option>
             <option value="SUBMITTED">Submitted</option>
             <option value="PENDING">Pending</option>
             <option value="REUPDATED">Reupdated</option>
             <option value="REJECTED">Rejected</option>
             <option value="APPROVED">Approved</option>
           </select>

           <select
             value={roleFilter}
             onChange={(e) => setRoleFilter(e.target.value)}
             className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 bg-white"
           >
             <option value="ALL">All Roles</option>
             <option value="EMPLOYEE">Employee</option>
             <option value="MANAGER">Manager</option>
           </select>

           <div className="relative">
             <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
             <input
               type="date"
               placeholder="Start Date"
               value={startDate}
               onChange={(e) => setStartDate(e.target.value)}
               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900"
             />
           </div>

           <div className="relative">
             <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
             <input
               type="date"
               placeholder="End Date"
               value={endDate}
               onChange={(e) => setEndDate(e.target.value)}
               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900"
             />
           </div>
         </div>

         {/* Quick Date Presets */}
         <div className="mt-4">
           <div className="flex items-center space-x-2 mb-2">
             <span className="text-sm font-medium text-gray-700">Quick Filters:</span>
             <button
               onClick={() => {
                 const today = new Date();
                 const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                 setStartDate(weekAgo.toISOString().split('T')[0]);
                 setEndDate(today.toISOString().split('T')[0]);
               }}
               className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
             >
               Last 7 Days
             </button>
             <button
               onClick={() => {
                 const today = new Date();
                 const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                 setStartDate(monthAgo.toISOString().split('T')[0]);
                 setEndDate(today.toISOString().split('T')[0]);
               }}
               className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
             >
               Last 30 Days
             </button>
             <button
               onClick={() => {
                 const today = new Date();
                 const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                 setStartDate(startOfMonth.toISOString().split('T')[0]);
                 setEndDate(today.toISOString().split('T')[0]);
               }}
               className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
             >
               This Month
             </button>
             <button
               onClick={() => {
                 const today = new Date();
                 const startOfWeek = new Date(today);
                 startOfWeek.setDate(today.getDate() - today.getDay());
                 setStartDate(startOfWeek.toISOString().split('T')[0]);
                 setEndDate(today.toISOString().split('T')[0]);
               }}
               className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
             >
               This Week
             </button>
           </div>
         </div>

          {/* Filter Actions */}
          {(startDate || endDate || searchTerm || timesheetIdSearch || statusFilter !== 'ALL') && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  Showing {filteredTimesheets.length} of {total || 0} timesheets (Page {currentPage + 1} of {totalPages || 1})
                </span>
                {(startDate || endDate) && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Date Filter: {startDate && endDate ? `${startDate} to ${endDate}` : startDate ? `From ${startDate}` : `Until ${endDate}`}
                  </span>
                )}
                {searchTerm && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    Employee: "{searchTerm}"
                  </span>
                )}
                {timesheetIdSearch && (
                  <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                    ID: "{timesheetIdSearch}"
                  </span>
                )}
                {statusFilter !== 'ALL' && (
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                    Status: {statusFilter}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setTimesheetIdSearch('');
                  setStatusFilter('ALL');
                  setStartDate('');
                  setEndDate('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <X className="w-4 h-4" />
                <span>Clear Filters</span>
              </button>
            </div>
          )}
      </motion.div>

      {/* Timesheets List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
         <div className="px-6 py-4 border-b border-gray-200">
           <div className="flex items-center justify-between">
             <h2 className="text-lg font-medium text-gray-900">
               Timesheets ({sortedTimesheets.length})
             </h2>
           </div>
         </div>
         <div className="divide-y divide-gray-200">
           {/* Clickable rows indicator */}
           <div className="px-6 py-2 bg-blue-50 border-b border-blue-200">
             <p className="text-xs text-blue-600 flex items-center">
               <FileText className="w-3 h-3 mr-1" />
               Click on any timesheet row to view full details
             </p>
           </div>
           {sortedTimesheets.map((timesheet, index) => (
             <motion.div
               key={timesheet.id}
               initial={{ opacity: 0, x: -20 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ delay: index * 0.1 }}
              onClick={() => {
                const detailUrl = user?.role === 'SUPER_ADMIN' 
                  ? `/super-admin/timesheets/${timesheet.id}` 
                  : `/admin/timesheets/${timesheet.id}`;
                navigate(detailUrl);
              }}
               className="px-4 md:px-6 py-3 md:py-4 hover:bg-gray-50 hover:border-l-4 hover:border-primary-500 transition-all duration-200 cursor-pointer group relative"
             >
               <div className="flex items-center justify-between gap-3">
                 <div className="flex items-center space-x-3 md:space-x-4 min-w-0 flex-1">
                   {/* Avatar - hidden on mobile */}
                   <span className="hidden md:block flex-shrink-0">
                     <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                       <span className="text-sm font-medium text-white">
                         {timesheet.employee?.firstName?.charAt(0)}{timesheet.employee?.lastName?.charAt(0)}
                       </span>
                     </div>
                   </span>
                   <div className="min-w-0 flex-1">
                     {/* Name + Timesheet ID - always visible (mobile shows only this line) */}
                     <div className="flex flex-wrap items-center gap-2">
                       <h3 
                         onClick={(e) => {
                           e.stopPropagation();
                           const userId = timesheet.employee?.id;
                           if (userId) {
                             const detailUrl = user?.role === 'SUPER_ADMIN' 
                               ? `/super-admin/users/${userId}/timesheets` 
                               : `/admin/users/${userId}/timesheets`;
                             navigate(detailUrl);
                           }
                         }}
                         className="text-sm font-medium text-blue-600 group-hover:text-blue-700 transition-colors flex items-center cursor-pointer hover:underline"
                       >
                         {timesheet.employee?.firstName} {timesheet.employee?.lastName}
                         <span className="ml-2 text-gray-400 group-hover:text-primary-500 transition-colors hidden md:inline">
                           →
                         </span>
                       </h3>
                       <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded border border-gray-300">
                         ID: {timesheet.id}
                       </span>
                     </div>
                     {/* Email, week, hours, entries - hidden on mobile */}
                     <p className="text-sm text-gray-500 hidden md:block">{timesheet.employee?.email}</p>
                     <div className="hidden md:flex items-center space-x-4 mt-1">
                       <span className="text-xs text-gray-500">
                         Week of {new Date(timesheet.startDate || timesheet.weekStartDate).toLocaleDateString()}
                       </span>
                       <span className="text-xs text-gray-500">
                         {calculateTotalHours(timesheet.entries)} hours
                       </span>
                       <span className="text-xs text-gray-500">
                         {timesheet.entries?.length || 0} entries
                       </span>
                     </div>
                     <div className="mt-2 flex items-center space-x-4 max-md:hidden">
                       <span className="text-xs text-gray-400">
                         Last updated: {timesheet.updatedAt ? new Date(timesheet.updatedAt).toLocaleDateString() : 'N/A'}
                       </span>
                       {timesheet.status === 'APPROVED' && (
                         <span className="text-xs text-green-600 font-medium">
                           ✓ Approved
                         </span>
                       )}
                       {timesheet.status === 'PENDING' && (
                         <span className="text-xs text-orange-600 font-medium">
                           ⚠ Needs Correction
                         </span>
                       )}
                       {timesheet.status === 'REUPDATED' && (
                         <span className="text-xs text-blue-600 font-medium">
                           🔄 Updated & Resubmitted
                         </span>
                       )}
                       {timesheet.status === 'REJECTED' && (
                         <span className="text-xs text-red-600 font-medium">
                           ✗ Rejected
                         </span>
                       )}
                     </div>
                   </div>
                 </div>
                 <div className="flex items-center space-x-4">
                   <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(timesheet.status)}`}>
                     {getStatusIcon(timesheet.status)}
                     <span>{timesheet.status}</span>
                   </div>
                   <div className="flex items-center space-x-2">
                     {(timesheet.status === 'SUBMITTED' || timesheet.status === 'REUPDATED' || timesheet.status === 'DRAFT') && (
                       <>
                         <button
                           onClick={(e) => {
                             e.stopPropagation(); // Prevent row click
                             setSelectedTimesheet(timesheet);
                             handleApprove();
                           }}
                           disabled={approveMutation.isLoading}
                           className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                           title="Quick Approve"
                         >
                           <Check className="w-4 h-4" />
                         </button>
                         <button
                           onClick={(e) => {
                             e.stopPropagation(); // Prevent row click
                             setSelectedTimesheet(timesheet);
                             handleReject();
                           }}
                           disabled={rejectMutation.isLoading}
                           className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                           title="Quick Reject"
                         >
                           <X className="w-4 h-4" />
                         </button>
                       </>
                     )}
                     <button
                       onClick={(e) => {
                         e.stopPropagation(); // Prevent row click
                         const detailUrl = user?.role === 'SUPER_ADMIN' 
                           ? `/super-admin/timesheets/${timesheet.id}` 
                           : `/admin/timesheets/${timesheet.id}`;
                         navigate(detailUrl);
                       }}
                       className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                       title="Open Full Details"
                     >
                       <FileText className="w-4 h-4" />
                     </button>
                   </div>
                 </div>
               </div>
             </motion.div>
           ))}
        </div>
        {sortedTimesheets.length === 0 && (
          <div className="px-6 py-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'ALL' || startDate || endDate
                ? 'Try adjusting your search or filter criteria.'
                : 'No timesheets have been submitted yet.'
              }
            </p>
          </div>
        )}
      </motion.div>

      {/* Timesheet Details Modal */}
      {showDetails && selectedTimesheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Timesheet Details</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {/* Employee Information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Employee Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Full Name</span>
                      <p className="text-sm text-gray-900">
                        {selectedTimesheet.employee?.firstName} {selectedTimesheet.employee?.lastName}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Email Address</span>
                      <p className="text-sm text-gray-900">{selectedTimesheet.employee?.email}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Employee ID</span>
                      <p className="text-sm text-gray-900">{selectedTimesheet.employee?.id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Username</span>
                      <p className="text-sm text-gray-900">{selectedTimesheet.employee?.username || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timesheet Information */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Timesheet Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Week Period</span>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedTimesheet.weekStartDate).toLocaleDateString()} - {new Date(selectedTimesheet.weekEndDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Status</span>
                    <div className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 w-fit ${getStatusColor(selectedTimesheet.status)}`}>
                        {getStatusIcon(selectedTimesheet.status)}
                        <span>{selectedTimesheet.status}</span>
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Total Hours</span>
                    <p className="text-sm text-gray-900 font-semibold">{calculateTotalHours(selectedTimesheet.entries)} hours</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Submitted Date</span>
                    <p className="text-sm text-gray-900">
                      {selectedTimesheet.submittedAt ? new Date(selectedTimesheet.submittedAt).toLocaleString() : 'Not submitted'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Last Updated</span>
                    <p className="text-sm text-gray-900">
                      {selectedTimesheet.updatedAt ? new Date(selectedTimesheet.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Weekly Timesheet Format */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Weekly Timesheet - {new Date(selectedTimesheet.weekStartDate).toLocaleDateString()} to {new Date(selectedTimesheet.weekEndDate).toLocaleDateString()}
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Daily task breakdown and hours verification
                  </p>
                </div>
                
                {/* Weekly Timesheet Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          Tasks
                        </th>
                        {(() => {
                          const days = [];
                          const startDate = new Date(selectedTimesheet.weekStartDate);
                          const endDate = new Date(selectedTimesheet.weekEndDate);
                          
                          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                            days.push(new Date(d));
                          }
                          
                          return days.map((day, index) => (
                            <th key={index} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                              <div>{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                              <div className="text-xs text-gray-400">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                            </th>
                          ));
                        })()}
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {(() => {
                        // Group entries by task/project
                        const taskGroups = {};
                        selectedTimesheet.entries?.forEach(entry => {
                          const taskKey = entry.project || 'No Project';
                          if (!taskGroups[taskKey]) {
                            taskGroups[taskKey] = [];
                          }
                          taskGroups[taskKey].push(entry);
                        });

                        const startDate = new Date(selectedTimesheet.weekStartDate);
                        const endDate = new Date(selectedTimesheet.weekEndDate);
                        const days = [];
                        
                        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                          days.push(new Date(d));
                        }

                        return Object.entries(taskGroups).map(([taskName, entries], taskIndex) => {
                          const taskTotal = entries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                          
                          return (
                            <tr key={taskIndex} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                <div className="font-semibold">{taskName}</div>
                                {entries[0]?.description && (
                                  <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                                    {entries[0].description}
                                  </div>
                                )}
                              </td>
                              {days.map((day, dayIndex) => {
                                const dayEntry = entries.find(entry => 
                                  new Date(entry.date).toDateString() === day.toDateString()
                                );
                                const hours = dayEntry?.hours || 0;
                                
                                return (
                                  <td key={dayIndex} className="px-4 py-3 text-center text-sm text-gray-900 border-l border-gray-200">
                                    <div className={`font-medium ${hours > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                      {hours > 0 ? `${hours.toFixed(1)}h` : '-'}
                                    </div>
                                    {dayEntry?.startTime && dayEntry?.endTime && (
                                      <div className="text-xs text-gray-500">
                                        {dayEntry.startTime}-{dayEntry.endTime}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-3 text-center text-sm font-bold text-gray-900 border-l border-gray-200 bg-gray-50">
                                {taskTotal.toFixed(1)}h
                              </td>
                            </tr>
                          );
                        });
                      })()}
                      
                      {/* Total Row */}
                      <tr className="border-t-2 border-gray-300 bg-blue-50">
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">
                          <div className="flex items-center">
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Total Hours
                          </div>
                        </td>
                        {(() => {
                          const startDate = new Date(selectedTimesheet.weekStartDate);
                          const endDate = new Date(selectedTimesheet.weekEndDate);
                          const days = [];
                          
                          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                            days.push(new Date(d));
                          }

                          return days.map((day, dayIndex) => {
                            const dayTotal = selectedTimesheet.entries?.filter(entry => 
                              new Date(entry.date).toDateString() === day.toDateString()
                            ).reduce((sum, entry) => sum + (entry.hours || 0), 0) || 0;
                            
                            return (
                              <td key={dayIndex} className="px-4 py-3 text-center text-sm font-bold text-gray-900 border-l border-gray-200">
                                <div className={`${dayTotal > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                  {dayTotal > 0 ? `${dayTotal.toFixed(1)}h` : '-'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {dayTotal >= 8 ? 'Full' : dayTotal >= 4 ? 'Half' : dayTotal > 0 ? 'Partial' : 'Empty'}
                                </div>
                              </td>
                            );
                          });
                        })()}
                        <td className="px-4 py-3 text-center text-sm font-bold text-blue-600 border-l border-gray-200">
                          {calculateTotalHours(selectedTimesheet.entries).toFixed(1)}h
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {selectedTimesheet.entries?.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheet entries</h3>
                    <p className="mt-1 text-sm text-gray-500">No tasks have been logged for this timesheet.</p>
                  </div>
                )}
              </div>

              {/* Task Descriptions - Enhanced for Verification */}
              {selectedTimesheet.entries?.length > 0 && (
                <div className="bg-white border-2 border-blue-200 rounded-lg p-6 mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xl font-bold text-gray-900 flex items-center">
                      <FileText className="w-6 h-6 mr-3 text-blue-600" />
                      Task Descriptions for Verification
                    </h4>
                    <div className="text-sm text-gray-500 bg-blue-50 px-3 py-1 rounded-full">
                      {selectedTimesheet.entries?.length || 0} tasks to review
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Verification Instructions
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Please review each task description carefully. Check that:</p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>Task descriptions are clear and detailed</li>
                            <li>Hours logged match the work described</li>
                            <li>Project assignments are correct</li>
                            <li>Time entries are reasonable and complete</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {(() => {
                    // Group entries by date and then by task
                    const dateGroups = {};
                    selectedTimesheet.entries?.forEach(entry => {
                      const dateKey = new Date(entry.date).toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                      if (!dateGroups[dateKey]) {
                        dateGroups[dateKey] = [];
                      }
                      dateGroups[dateKey].push(entry);
                    });

                    return Object.entries(dateGroups)
                      .sort(([a], [b]) => new Date(a) - new Date(b))
                      .map(([date, entries]) => (
                        <div key={date} className="mb-8 last:mb-0">
                          <div className="bg-blue-600 text-white rounded-t-lg px-4 py-3">
                            <h5 className="text-lg font-semibold flex items-center">
                              <Calendar className="w-5 h-5 mr-2" />
                              {date}
                            </h5>
                            <p className="text-blue-100 text-sm mt-1">
                              {entries.length} task{entries.length !== 1 ? 's' : ''} • {entries.reduce((sum, entry) => sum + (entry.hours || 0), 0).toFixed(1)} total hours
                            </p>
                          </div>
                          <div className="bg-white border border-blue-200 rounded-b-lg">
                            <div className="space-y-0">
                              {entries.map((entry, index) => (
                                <div key={index} className={`p-6 border-b border-gray-100 last:border-b-0 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-3 mb-3">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                          {entry.project || 'No Project'}
                                        </span>
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                          {entry.hours || 0} hours
                                        </span>
                                        {entry.taskName && (
                                          <span className="text-sm text-gray-600 font-medium">
                                            Task: {entry.taskName}
                                          </span>
                                        )}
                                      </div>
                                      
                                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <h6 className="text-sm font-semibold text-gray-700 mb-2">Task Description:</h6>
                                        <p className="text-gray-800 leading-relaxed">
                                          {entry.description || 'No description provided'}
                                        </p>
                                        {entry.notes && (
                                          <div className="mt-3 pt-3 border-t border-gray-200">
                                            <h6 className="text-sm font-semibold text-gray-700 mb-1">Additional Notes:</h6>
                                            <p className="text-sm text-gray-600 italic">
                                              {entry.notes}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="ml-6 text-right">
                                      <div className="bg-gray-100 rounded-lg p-3 min-w-[120px]">
                                        <div className="text-xs text-gray-500 mb-1">Time Range</div>
                                        {entry.startTime && entry.endTime ? (
                                          <div className="text-sm font-medium text-gray-800">
                                            {entry.startTime} - {entry.endTime}
                                          </div>
                                        ) : (
                                          <div className="text-sm text-gray-500 italic">
                                            No time specified
                                          </div>
                                        )}
                                        <div className="text-xs text-gray-500 mt-2">
                                          Status: {entry.hours > 0 ? 'Completed' : 'No time logged'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ));
                  })()}
                </div>
              )}

              {/* Task Summary */}
              {selectedTimesheet.entries?.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4 mt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Weekly Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedTimesheet.entries?.length || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {calculateTotalHours(selectedTimesheet.entries).toFixed(1)}h
                      </div>
                      <div className="text-sm text-gray-600">Total Hours</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {selectedTimesheet.entries?.filter(entry => entry.hours > 0).length || 0}
                      </div>
                      <div className="text-sm text-gray-600">Completed Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {(() => {
                          const startDate = new Date(selectedTimesheet.weekStartDate);
                          const endDate = new Date(selectedTimesheet.weekEndDate);
                          const days = [];
                          
                          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                            days.push(new Date(d));
                          }
                          
                          return days.filter(day => {
                            const dayTotal = selectedTimesheet.entries?.filter(entry => 
                              new Date(entry.date).toDateString() === day.toDateString()
                            ).reduce((sum, entry) => sum + (entry.hours || 0), 0) || 0;
                            return dayTotal >= 8;
                          }).length;
                        })()}
                      </div>
                      <div className="text-sm text-gray-600">Full Days</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Update History */}
              <div className="bg-yellow-50 rounded-lg p-4 mt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Status Update History
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Timesheet Created</div>
                      <div className="text-sm text-gray-500">
                        {selectedTimesheet.createdAt ? new Date(selectedTimesheet.createdAt).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      Status: <span className="font-medium text-gray-900">DRAFT</span>
                    </div>
                  </div>

                  {selectedTimesheet.submittedAt && (
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-yellow-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Timesheet Submitted</div>
                        <div className="text-sm text-gray-500">
                          {new Date(selectedTimesheet.submittedAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Status: <span className="font-medium text-yellow-600">SUBMITTED</span>
                      </div>
                    </div>
                  )}

                  {selectedTimesheet.status === 'APPROVED' && (
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-200">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Timesheet Approved</div>
                        <div className="text-sm text-gray-500">
                          {selectedTimesheet.updatedAt ? new Date(selectedTimesheet.updatedAt).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Status: <span className="font-medium text-green-600">APPROVED</span>
                      </div>
                    </div>
                  )}

                  {selectedTimesheet.status === 'REJECTED' && (
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-red-200">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <XCircle className="w-4 h-4 text-red-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Timesheet Rejected</div>
                        <div className="text-sm text-gray-500">
                          {selectedTimesheet.updatedAt ? new Date(selectedTimesheet.updatedAt).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Status: <span className="font-medium text-red-600">REJECTED</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Update Section - Enhanced */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                    Status Update After Review
                  </h4>
                  <div className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border">
                    Current Status: <span className="font-semibold">{selectedTimesheet.status}</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 mb-4 border border-blue-200">
                  <p className="text-sm text-gray-700 mb-3">
                    After reviewing all task descriptions and verifying the timesheet details, you can now update the status:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700"><strong>Approve:</strong> Timesheet is accurate and complete</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-gray-700"><strong>Reject:</strong> Issues found, needs revision</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  {(selectedTimesheet.status === 'SUBMITTED' || selectedTimesheet.status === 'PENDING' || selectedTimesheet.status === 'REUPDATED' || selectedTimesheet.status === 'DRAFT') && (
                    <>
                      <button
                        onClick={handleReject}
                        disabled={rejectMutation.isLoading}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2 font-medium"
                      >
                        <X className="w-4 h-4" />
                        <span>Reject Timesheet</span>
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={approveMutation.isLoading}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2 font-medium"
                      >
                        <Check className="w-4 h-4" />
                        <span>Approve Timesheet</span>
                      </button>
                    </>
                  )}
                  
                  {selectedTimesheet.status === 'APPROVED' && (
                    <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">This timesheet has been approved</span>
                    </div>
                  )}
                  
                  {selectedTimesheet.status === 'REJECTED' && (
                    <div className="flex items-center space-x-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                      <XCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">This timesheet has been rejected</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    <span>Close Review</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Reject Timesheet
              </h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this timesheet..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={4}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReject}
                  disabled={rejectMutation.isLoading || !rejectionReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Reject Timesheet</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TimesheetReview;
